import { getUnitReviewReasonLabel, type UnitReviewCategory, type UnitReviewReason } from './unitReviewOptions'

export interface UnitReviewRecord {
    /** Legacy projection of the first selected canonical reason. */
    category?: 'temporary' | 'permanent'
    reason?: string
    label?: string
    temporaryReasons: UnitReviewReason[]
    permanentReasons: UnitReviewReason[]
    updatedAt: string
    updatedBy: number | string | null
    [key: string]: unknown
}

export interface OffMarketActivityEvent {
    type: 'off_market'
    reason: 'other_offer_completed' | 'landlord_removed_unit' | 'other'
    offerId?: number
    outcome?: 'rented' | 'sold'
    source?: string
    note?: string
    createdAt: string
}

export interface RelistActivityEvent {
    type: 'relisted'
    direction: 'from' | 'to'
    sourceUnitId: number
    targetUnitId: number
    source?: string
    createdAt: string
}

export type UnitActivityEvent = OffMarketActivityEvent | RelistActivityEvent

export interface NormalizedUnitReview {
    review?: UnitReviewRecord
    offMarket: { events: OffMarketActivityEvent[] }
    relist: { events: RelistActivityEvent[] }
    [key: string]: unknown
}

const asObject = (value: unknown): Record<string, unknown> =>
    value != null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}

const asArray = (value: unknown): unknown[] => Array.isArray(value) ? value : []

const pickReview = (value: unknown): UnitReviewRecord | undefined => {
    const review = asObject(value)
    const hasTemporaryReasons = Object.prototype.hasOwnProperty.call(review, 'temporaryReasons')
    const hasPermanentReasons = Object.prototype.hasOwnProperty.call(review, 'permanentReasons')
    const normalizeReasons = (category: UnitReviewCategory, reasons: unknown): UnitReviewReason[] => {
        const values = typeof reasons === 'string' ? [reasons] : asArray(reasons)
        return values.reduce<UnitReviewReason[]>((result, item) => {
            const itemObject = asObject(item)
            const value = typeof item === 'string' ? item.trim() : typeof itemObject.value === 'string' ? itemObject.value.trim() : ''
            if (value === '' || result.some((reason) => reason.value === value)) return result
            const label = typeof itemObject.label === 'string' ? itemObject.label : getUnitReviewReasonLabel(category, value)
            return [...result, { value, label }]
        }, [])
    }
    let temporaryReasons = normalizeReasons('temporary', review.temporaryReasons)
    let permanentReasons = normalizeReasons('permanent', review.permanentReasons)
    const legacyCategory = review.category === 'temporary' || review.category === 'permanent' ? review.category : undefined
    const legacyReason = typeof review.reason === 'string' ? review.reason.trim() : ''

    // A missing canonical group may still be represented by the legacy scalar.
    if (!hasTemporaryReasons && legacyCategory === 'temporary' && legacyReason !== '') temporaryReasons = [{ value: legacyReason, label: typeof review.label === 'string' ? review.label : getUnitReviewReasonLabel('temporary', legacyReason) }]
    if (!hasPermanentReasons && legacyCategory === 'permanent' && legacyReason !== '') permanentReasons = [{ value: legacyReason, label: typeof review.label === 'string' ? review.label : getUnitReviewReasonLabel('permanent', legacyReason) }]

    const primaryCategory = temporaryReasons.length > 0 ? 'temporary' : permanentReasons.length > 0 ? 'permanent' : undefined
    const primaryReason = primaryCategory === 'temporary' ? temporaryReasons[0] : primaryCategory === 'permanent' ? permanentReasons[0] : undefined
    const { category: ignoredCategory, reason: ignoredReason, label: ignoredLabel, temporaryReasons: ignoredTemporaryReasons, permanentReasons: ignoredPermanentReasons, updatedAt: ignoredUpdatedAt, updatedBy: ignoredUpdatedBy, ...unknown } = review
    const hasReviewData = hasTemporaryReasons || hasPermanentReasons || (legacyCategory != null && legacyReason !== '') || Object.keys(unknown).length > 0 || 'updatedAt' in review || 'updatedBy' in review
    if (!hasReviewData) return undefined

    return {
        ...unknown,
        temporaryReasons,
        permanentReasons,
        ...(primaryCategory == null || primaryReason == null ? {} : {
            category: primaryCategory,
            reason: primaryReason.value,
            label: primaryReason.label
        }),
        updatedAt: typeof review.updatedAt === 'string' ? review.updatedAt : '',
        updatedBy: typeof review.updatedBy === 'number' || typeof review.updatedBy === 'string' ? review.updatedBy : null
    }
}

const asOffMarketEvent = (value: unknown): OffMarketActivityEvent | null => {
    const event = asObject(value)
    if (event.type !== 'off_market' || typeof event.reason !== 'string' || typeof event.createdAt !== 'string') return null
    return event as unknown as OffMarketActivityEvent
}

const asRelistEvent = (value: unknown): RelistActivityEvent | null => {
    const event = asObject(value)
    if (event.type !== 'relisted' || (event.direction !== 'from' && event.direction !== 'to') || typeof event.createdAt !== 'string') return null
    const sourceUnitId = Number(event.sourceUnitId)
    const targetUnitId = Number(event.targetUnitId)
    return Number.isFinite(sourceUnitId) && Number.isFinite(targetUnitId)
        ? { ...event, sourceUnitId, targetUnitId } as unknown as RelistActivityEvent
        : null
}

const legacyEvents = (value: Record<string, unknown>): unknown[] => {
    const activity = asObject(value.activity)
    return Array.isArray(activity.events) ? activity.events : []
}

/** Reads legacy flat/activity data and returns one non-mutating canonical document. */
export const normalizeUnitReview = (value: unknown): NormalizedUnitReview => {
    const source = asObject(value)
    const reviewValue = pickReview(source.review) ?? pickReview(source)
    const canonicalOffMarket = asArray(asObject(source.offMarket).events)
    const canonicalRelist = asArray(asObject(source.relist).events)
    const allEvents = [...canonicalOffMarket, ...canonicalRelist, ...legacyEvents(source)]
    const offMarket: OffMarketActivityEvent[] = []
    const relist: RelistActivityEvent[] = []
    allEvents.forEach((event) => {
        const offMarketEvent = asOffMarketEvent(event)
        if (offMarketEvent != null && !offMarket.some((item) => item.reason === offMarketEvent.reason && item.offerId === offMarketEvent.offerId && item.createdAt === offMarketEvent.createdAt)) offMarket.push(offMarketEvent)
        const relistEvent = asRelistEvent(event)
        if (relistEvent != null && !relist.some((item) => item.direction === relistEvent.direction && item.sourceUnitId === relistEvent.sourceUnitId && item.targetUnitId === relistEvent.targetUnitId && item.createdAt === relistEvent.createdAt)) relist.push(relistEvent)
    })
    const { category, reason, label, updatedAt, updatedBy, activity, review, offMarket: ignoredOffMarket, relist: ignoredRelist, ...unknown } = source
    return {
        ...unknown,
        ...(reviewValue == null ? {} : { review: reviewValue }),
        offMarket: { events: offMarket },
        relist: { events: relist }
    }
}

export const updateUnitReviewSection = (value: unknown, review: UnitReviewRecord): NormalizedUnitReview => ({
    ...normalizeUnitReview(value),
    review
})

/** Merges only the review branch; all other shared-document branches remain top-level replacement semantics. */
export const mergeUnitReview = (current: unknown, patch: unknown): NormalizedUnitReview => {
    const currentDocument = asObject(current)
    const patchDocument = asObject(patch)
    const merged: Record<string, unknown> = { ...currentDocument, ...patchDocument }
    if (Object.prototype.hasOwnProperty.call(patchDocument, 'review')) {
        merged.review = { ...asObject(currentDocument.review), ...asObject(patchDocument.review) }
    }
    return normalizeUnitReview(merged)
}

/** Appends to the event's domain section without changing review or the other section. */
export const appendUnitReviewActivity = (value: unknown, event: UnitActivityEvent): NormalizedUnitReview => {
    const normalized = normalizeUnitReview(value)
    if (event.type === 'off_market') {
        const exists = event.reason === 'other_offer_completed' && event.offerId != null && normalized.offMarket.events.some((item) => item.reason === event.reason && item.offerId === event.offerId)
        return exists ? normalized : { ...normalized, offMarket: { events: [...normalized.offMarket.events, event] } }
    }
    return { ...normalized, relist: { events: [...normalized.relist.events, event] } }
}

export const getUnitReviewActivityEvents = (value: unknown): UnitActivityEvent[] => {
    const normalized = normalizeUnitReview(value)
    return [...normalized.offMarket.events, ...normalized.relist.events]
}

export const getRelatedUnitId = (event: RelistActivityEvent): number => event.direction === 'to' ? event.targetUnitId : event.sourceUnitId

export const getRelistTargetUnitId = (value: unknown): number | null => {
    const event = normalizeUnitReview(value).relist.events.find((item) => item.direction === 'to')
    return event == null ? null : event.targetUnitId
}
